<template>
  <div class="comparison-wrapper">
    <!-- Header Section -->
    <div class="header-section">
      <h2 class="main-title">{{ columnData.mainTitle }}</h2>
      <p class="sub-title">{{ columnData.subTitle }}</p>
    </div>

    <!-- Compare Table -->
    <div class="comparison-table-section">
      <!-- Fixed Section-->
      <div class="fixed-columns">
        <div class="category-col">
          <div class="table-header">
            <div class="cell category">Category</div>
          </div>
          <div class="table-body">
            <div
              class="table-row"
              v-for="item in comparisons"
              :key="item.uid"
            >
              <div class="cell category">{{ item.name }}</div>
            </div>
          </div>
        </div>
        <div class="think-col">
          <div class="table-header">
            <div class="cell think">Think Academy</div>
          </div>
          <div class="table-body">
            <div
              class="table-row"
              v-for="item in comparisons"
              :key="item.uid"
            >
              <div class="cell think">
                <span v-if="item.showSymbol" class="check"></span>
                <span style="white-space: pre-line;">{{ item.thinkDesc }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Swiper Section -->
      <div class="swiper-section">
        <div
          ref="swiper"
          v-swiper:mySwiper="swiperOption"
          class="swiper-container"
          @mouseenter="handleMouseenter"
          @mouseleave="handleMouseleave"
        >
          <div class="swiper-wrapper">
            <div v-for="(competitor, index) in competitors" :key="competitor.uid" class="swiper-slide comparison-col">
              <div class="table-header">
                <div class="cell competitor">{{ competitor.name }}</div>
              </div>
              <div class="table-body">
                <div
                  class="table-row table-row-competitor"
                  v-for="(item) in comparisons"
                  :key="item.uid"
                >
                <div class="cell competitor">
                    <span v-if="item.showSymbol" class="cross"></span>
                    <span style="white-space: pre-line;">{{ item.competitorDescs[index] }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Pre Button -->
      <div v-if="competitors.length > 1" class="swiper-button-prev swiper-custom-prev" @click="goToPrev">
        <span class="arrow"></span>
      </div>
      <!-- Next Button -->
      <div v-if="competitors.length > 1" class="swiper-button-next swiper-custom-next" @click="goToNext">
        <span class="arrow"></span>
      </div>
    </div>

    <!-- pagination -->
    <div v-if="competitors.length > 1" class="swiper-pagination" />

    <!-- button -->
    <div class="btn-wrapper" @click="handleToLink(btn)">
      {{ btn.text }}
    </div>

    <!-- bg-blur -->
    <div class="comparison-top-bg"></div>
    <div class="comparison-bottom-bg"></div>
  </div>
</template>

<script>
import mixin from './mixin';
import { directive } from 'vue-awesome-swiper';
import bus from 'common/bus';

export default {
  directives: {
    swiper: directive,
  },
  mixins: [mixin],
  data() {
    return {
      swiperOption: this.getSwiperOption(),
      isHovered: false,
      mySwiper: null,
      currentSlide: 0,
    };
  },
  mounted() {
    // center single image
    if (this.competitors && this.competitors.length === 1) {
      const swiperContainer = this.$el.querySelector('.swiper-container');
      if (swiperContainer) {
        swiperContainer.style.display = 'flex';
        swiperContainer.style.justifyContent = 'center';
      }
    }

    // Initialize swiper and set up listeners
    this.$nextTick(() => {
      const refInst = this.$refs.swiperRef && this.$refs.swiperRef.swiper;
      if (refInst) {
        this.mySwiper = refInst;
      } else if (this.mySwiper == null && this.$refs.swiper) {
        this.mySwiper = this.$refs.swiper.swiper || this.$refs.swiper;
      }

      if (this.mySwiper && this.competitors && this.competitors.length > 1) {
        this.mySwiper.on('slideChangeTransitionEnd', this.syncRowHeights);
      }

      this.syncRowHeights();
    });

    window.addEventListener('resize', this.syncRowHeights);
  },
  updated() {
    this.$nextTick(() => {
      this.syncRowHeights();
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.syncRowHeights);
    if (this.mySwiper) {
      this.mySwiper.off('slideChangeTransitionEnd', this.syncRowHeights);
    }
  },
  methods: {
    pauseAutoPlay() {
      if (this.mySwiper && this.mySwiper.autoplay) this.mySwiper.autoplay.stop();
    },
    resumeAutoPlay() {
      if (this.mySwiper && this.mySwiper.autoplay) this.mySwiper.autoplay.start();
    },
    getSwiperOption() {
      const hasMultiple = this.competitors && this.competitors.length > 1;
      const isLargeScreen = typeof window !== 'undefined' && window.innerWidth >= 900;
      const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
      const loopedSlides = this.competitors ? this.competitors.length : 0;

      return {
        slidesPerView: 'auto',
        centeredSlides: true,
        loop: hasMultiple,
        loopedSlides: hasMultiple ? loopedSlides : 0,
        autoplay: hasMultiple
          ? {
              delay: 5000,
              disableOnInteraction: false,
            }
          : false,
        speed: 500,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        observer: true,
        observeParents: true,
      };
    },
    handleMouseenter() {
      this.isHovered = true;
      if (this.mySwiper && this.mySwiper.autoplay) {
        this.mySwiper.autoplay.stop();
      }
    },
    handleMouseleave() {
      this.isHovered = false;
      const hasMultipleImages = this.competitors && this.competitors.length > 1;
      const isLargeScreen = typeof window !== 'undefined' && window.innerWidth >= 900;
      if (this.mySwiper && this.mySwiper.autoplay && hasMultipleImages && isLargeScreen) {
        this.mySwiper.autoplay.start();
      }
    },
    goToPrev() {
      if (this.mySwiper) {
        this.mySwiper.slidePrev();
      }
    },
    goToNext() {
      if (this.mySwiper) {
        this.mySwiper.slideNext();
      }
    },
    goToSlide(index) {
      if (!this.mySwiper) return;
      this.mySwiper.slideToLoop(index, 600);
    },
    handleSlideChange() {
      if (!this.mySwiper || !this.competitors || this.competitors.length <= 1) {
        return;
      }
      const currentIndex = this.mySwiper.activeIndex;
      const realLength = this.competitors.length;
      if (currentIndex === 0) {
        setTimeout(() => {
          this.mySwiper.slideTo(realLength, 0);
        }, 100);
      }
      if (currentIndex === realLength + 1) {
        setTimeout(() => {
          this.mySwiper.slideTo(1, 0);
        }, 100);
      }
    },
    handleToLink(item) {
      this.GAReport()
      if (item.skip === '_blank') {
        if (item.feature === '_blank') {
          if (!this.isLogin) {
            bus.$emit('login', { triggerSource: 'appointment' });
            return;
          }
          bus.$emit('toShowDiagnosis');
        } else {
          if (!this.$store.state.signupEntranceTriage) {
            const successUrl = `/triage-desk/${this.btn.triageComponentId}`
            if (!this.isLogin) {
              bus.$emit('login', { successUrl, triggerSource: 'down' })
              return
            }
            if (this.btn.triageComponentId) this.$router.push(successUrl)
            return
          }
          if (this.btn.triageComponentId) this.$router.push(`/new-triage-desk/${this.btn.triageComponentId}`)
        }
      }
      if (item.skip === '_self') {
        if (item.target === '_blank') {
          window.location.href = this.link;
        }
        if (item.target === '_self') {
          window.open(item.link, '_self');
        }
      }
    },

    /** Core logic: sync heights for each row; exclude the last row */
    syncRowHeights() {
      this.$nextTick(() => {
        const categoryRows = this.$el.querySelectorAll('.category-col .table-row');
        const thinkRows = this.$el.querySelectorAll('.think-col .table-row');
        const competitorColumns = this.$el.querySelectorAll('.comparison-col');

        if (!categoryRows.length) return;

        const rowCount = categoryRows.length;
        const effectiveCount = rowCount - 1; // Skip the last row

        for (let i = 0; i < effectiveCount; i++) {
          const relatedCells = [categoryRows[i], thinkRows[i]];
          competitorColumns.forEach((col) => {
            const competitorRow = col.querySelectorAll('.table-row')[i];
            if (competitorRow) relatedCells.push(competitorRow);
          });

          // Reset previous heights
          relatedCells.forEach((el) => (el.style.height = 'auto'));

          // Compute max height
          const maxHeight = Math.max(...relatedCells.map((el) => el.offsetHeight));

          // Apply unified height
          relatedCells.forEach((el) => (el.style.height = maxHeight + 'px'));
        }

        // Restore auto height for the last row
        [categoryRows[rowCount - 1], thinkRows[rowCount - 1]].forEach((el) => {
          if (el) el.style.height = 'auto';
        });
        competitorColumns.forEach((col) => {
          const lastRow = col.querySelectorAll('.table-row')[rowCount - 1];
          if (lastRow) lastRow.style.height = 'auto';
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.comparison-wrapper {
  max-width: 1280px;
  margin: 0 auto;
  padding: 100px 0;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  position: relative;

  .header-section {
    max-width: 1050px;
    margin: 0 auto 40px;
    .main-title {
      font-size: 52px;
      font-weight: 700;
      color: rgba(0, 0, 0, 0.85);
    }
    .sub-title {
      font-size: 16px;
      color: rgba(0, 0, 0, 0.85);
      margin-top: 12px;
    }
  }

  .comparison-table-section {
    max-width: 1107px;
    margin: 0 auto;
    display: flex;
    position: relative;
    z-index: 1;

    .fixed-columns {
      max-width: 726px;
      overflow: hidden;
      display: flex;
      z-index: 1;
    }
    .category-col {
      margin-top: 27px;
      .table-body {
        background: linear-gradient(193.36deg, #FFE2D1 2.92%, #FFCBAB 100%);
        border-top: 0;
        border: 1px solid rgba(255, 164, 106, 1);
        z-index: 1;
        width: 240px;
      }
    }

    .think-col {
      .table-body {
        width: 510px;
        margin-left: -24px;
        background: linear-gradient(184.9deg, #FFC665 0%, #FA8100 100%);
        border: 3px solid rgba(255, 90, 0, 1);
        border-top: 1px solid transparent;
        z-index: 2;
        .table-row {
          &:last-child {
            // height: 105px;
            .think {
              padding: 33px 40px;
            }
          }
        }
      }
    }

    .comparison-col {
      margin-top: 27px;
      .table-body {
        background: linear-gradient(135.91deg, #FFFFFF -1.3%, #F3F3F3 65.06%);
        border-top: 0;
        border: 1px solid #D6D6D6;
        z-index: 1;
        width: 394px;
      }
    }

    .cell {
      font-family: Poppins;
      font-weight: 700;
      font-style: Bold;
      letter-spacing: 0%;
      text-align: left;
      display: flex;
      align-items: center;
    }
    .table-header {
      // display: flex;
      // align-items: flex-end;
      .cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        font-weight: 700;
      }

      .category {
        width: 240px;
        height: 70px;
        background: linear-gradient(180deg, #FFB181 0%, #FF8F4A 100%);
        // border-bottom: 1px solid rgba(255, 255, 255, 0.3);
        padding: 20px 30px;
        font-size: 20px;
        color: rgba(0, 0, 0, 0.85);
        border-radius: 20px 20px 0 0;
        border-left: 1px solid rgba(255, 164, 106, 1);
        border-top: 1px solid rgba(255, 164, 106, 1);
        border-right: 1px solid rgba(255, 164, 106, 1);
        line-height: 70px;
      }

      .think {
        width: 510px;
        height: 97px;
        padding: 20px 40px;
        background: linear-gradient(0deg, #F95800 0%, #FF6D1D 100%);
        // border-bottom: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 20px 20px 0 0;
        border-left: 3px solid rgba(255, 90, 0, 1);
        border-top: 3px solid rgba(255, 90, 0, 1);
        border-right: 3px solid rgba(255, 90, 0, 1);
        font-size: 24px;
        line-height: 97px;
        margin-left: -24px;
        color: #fff;
      }

      .competitor {
        width: 394px;
        height: 70px;
        padding: 20px 40px;
        background: linear-gradient(180deg, #F2F2F2 0%, #DCDCDC 100%);
        border-radius: 20px 20px 0 0;
        border-top: 1px solid #D6D6D6;
        border-right: 1px solid #D6D6D6;
        font-size: 20px;
        // margin-left: -14px;
        color: rgba(0, 0, 0, 0.85);
      }
    }
    .table-body {
      display: flex;
      flex-direction: column;
      position: relative;
      border-radius: 0 0 20px 20px;
      overflow: hidden;
      .table-row {
        display: flex;
        min-height: 60px;
        height: auto;
        width: 100%;
        &:nth-child(odd) {
          background: transparent;
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
        }
        &:nth-child(even) {
          background: rgba(255, 255, 255, 0.1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
        }
        &:last-child {
          border-bottom: none;
        }
        .cell {
          width: 100%;
        }
        .category {
          padding: 20px 30px;
          font-size: 16px;
          color: rgba(0, 0, 0, 0.85);
          line-height: 20px;
        }
        .think {
          padding: 20px 40px;
          font-size: 16px;
          line-height: 20px;
          color: #fff;
        }
        .competitor {
          padding: 20px 40px;
          font-size: 16px;
          line-height: 20px;
          color: rgba(0, 0, 0, 0.85);
          font-weight: 400;
        }
      }
      .table-row-competitor {
        &:nth-child(even) {
          background: transparent;
          border-bottom: 1px solid rgba(255, 255, 255, 1)
        }
        &:nth-child(odd) {
          background: rgba(0, 0, 0, 0.03);
          border-bottom: 1px solid rgba(255, 255, 255, 1)
        }
        &:last-child {
          border-bottom: none;
        }
      }
    }

    .swiper-section {
      width: 394px;
      margin-left: -14px;
      z-index: 0;
    }

    .check {
      width: 20px;
      height: 20px;
      background: url('~assets/images/common/comparison-true.png') no-repeat;
      background-size: 100% 100%;
      margin-right: 10px;
      flex-shrink: 0;
    }
    .cross {
      width: 20px;
      height: 20px;
      background: url('~assets/images/common/comparison-false.png') no-repeat;
      background-size: 100% 100%;
      margin-right: 8px;
      flex-shrink: 0;
    }
  }

  .btn-wrapper {
    position: relative;
    z-index: 1;
    margin-top: 60px;
    padding: 20px 112px;
    border-radius: 100px;
    font-family: Poppins;
    font-weight: 700;
    font-style: Bold;
    font-size: 24px;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 90, 0, 1);
    cursor: pointer;
    transform: none;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 90, 0, 0.85);
      transform: scale(0.96);
    }
  }

  // .custom-pagination {
  //   position: relative;
  //   margin-top: 60px;
  //   display: flex;
  //   justify-content: center;
  //   gap: 8px;
  //   z-index: 1;
  //   .custom-bullet {
  //     width: 20px;
  //     height: 6px;
  //     border-radius: 8px;
  //     background: rgba(0, 0, 0, 0.1);
  //     cursor: pointer;
  //     transition: all 0.3s;
  //     &.active {
  //       background: #ff5a00;
  //     }
  //   }
  // }

  ::v-deep .swiper-pagination {
    position: relative !important;
    margin-top: 60px;
    display: flex;
    justify-content: center;
    gap: 8px;
    z-index: 1;
  }

  ::v-deep .swiper-pagination-bullet {
    width: 20px !important;
    height: 6px !important;
    border-radius: 8px !important;
    background: rgba(0, 0, 0, 0.1) !important;
    opacity: 1 !important;
  }

  ::v-deep .swiper-pagination-bullet-active {
    background: #FF5A00 !important;
  }

  ::v-deep .swiper-custom-prev,
  ::v-deep .swiper-custom-next {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.1);
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }

    &::after {
      display: none;
    }
  }

  ::v-deep .swiper-custom-prev {
    left: -86px;
     &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
    .arrow {
      width: 22px;
      height: 22px;
      background: url('~assets/images/common/comparison-prev.png') no-repeat;
      background-size: 100% 100%;
    }
  }
  ::v-deep .swiper-custom-next {
    right: -86px;
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
    .arrow {
      width: 22px;
      height: 22px;
      background: url('~assets/images/common/comparison-next.png') no-repeat;
      background-size: 100% 100%;
    }
  }

  .comparison-top-bg {
    position: absolute;
    width: 1600px;
    height: 682px;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background: url('~assets/images/common/comparison-top.webp') no-repeat;
    background-size: contain;
    background-position: bottom;
    z-index: 0;
  }
  .comparison-bottom-bg {
    position: absolute;
    width: 1600px;
    height: 707px;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    background: url('~assets/images/common/comparison-bottom.webp') no-repeat;
    background-size: contain;
    background-position: bottom;
    z-index: 0;
  }
}
@media (min-width: 1069px) and (max-width: 1279px) {
  .comparison-wrapper {
    max-width: 900px;
    .header-section {
      max-width: 800px;
    }

    .comparison-table-section {
      max-width: 900px;

      .fixed-columns {
        max-width: 584px;
      }
      .category-col {
        margin-top: 37px;
        .table-body {
          width: 190px;
        }
      }

      .think-col {
        .table-body {
          width: 402px;
          margin-left: -8px;
          z-index: 2;
          .table-row {
            &:last-child {
              // height: 105px;
              .think {
                padding: 22px 30px;
              }
            }
          }
        }
      }

      .comparison-col {
        margin-top: 37px;
        .table-body {
          width: 320px;
        }
      }
      .table-header {
        .category {
          width: 190px;
          height: 60px;
          padding: 20px;
          line-height: 60px;
        }

        .think {
          width: 402px;
          padding: 30px;
          margin-left: -8px;
        }

        .competitor {
          width: 320px;
          height: 60px;
          padding: 20px 30px;
        }
      }
      .table-body {
        .table-row {
          .category {
            padding: 20px;
          }
          .think {
            padding: 20px 30px;
          }
          .competitor {
            padding: 20px 30px;
          }
        }
      }

      .swiper-section {
        width: 320px;
        margin-left: -4px;
        z-index: 0;
      }
    }

    ::v-deep .swiper-custom-prev,
    ::v-deep .swiper-custom-next {
      display: flex !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    ::v-deep .swiper-custom-prev {
      left: -62px;
    }
    ::v-deep .swiper-custom-next {
      right: -62px;
    }
  }
}

@media (min-width: 900px) and (max-width: 1070px) {
  .comparison-wrapper {
    max-width: 900px;
    .header-section {
      max-width: 800px;
    }

    .comparison-table-section {
      max-width: 900px;

      .fixed-columns {
        max-width: 584px;
      }
      .category-col {
        margin-top: 37px;
        .table-body {
          width: 190px;
        }
      }

      .think-col {
        .table-body {
          width: 402px;
          margin-left: -8px;
          z-index: 2;
          .table-row {
            &:last-child {
              // height: 105px;
              .think {
                padding: 22px 30px;
              }
            }
          }
        }
      }

      .comparison-col {
        margin-top: 37px;
        .table-body {
          width: 320px;
        }
      }
      .table-header {
        .category {
          width: 190px;
          height: 60px;
          padding: 20px;
          line-height: 60px;
        }

        .think {
          width: 402px;
          padding: 30px;
          margin-left: -8px;
        }

        .competitor {
          width: 320px;
          height: 60px;
          padding: 20px 30px;
        }
      }
      .table-body {
        .table-row {
          .category {
            padding: 20px;
          }
          .think {
            padding: 20px 30px;
          }
          .competitor {
            padding: 20px 30px;
          }
        }
      }

      .swiper-section {
        width: 320px;
        margin-left: -4px;
        z-index: 0;
      }
    }

    ::v-deep .swiper-custom-prev,
    ::v-deep .swiper-custom-next {
      display: flex !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    ::v-deep .swiper-custom-prev {
      left: 12px;
    }
    ::v-deep .swiper-custom-next {
      right: 12px;
    }
  }
}

@media (max-width: 799px) {
  .comparison-wrapper {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 16vw 5.333vw;
    text-align: center;
    font-family: 'Poppins', sans-serif;
    position: relative;

    .header-section {
      max-width: 100%;
      margin: 0 auto 8vw;
      .main-title {
        font-size: 8.533vw;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.9);
      }
      .sub-title {
        margin-top: 1.6vw;
        font-size: 3.733vw;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.9);
        margin-top: 3.2vw;
      }
    }

    .comparison-table-section {
      max-width: 100%;
      margin: 0 auto;
      display: flex;
      position: relative;
      z-index: 1;

      .fixed-columns {
        overflow: hidden;
        display: flex;
        z-index: 1;
        max-width: 48.067vw;
      }

      .category-col {
        display: none;
      }

      .think-col {
        .table-body {
          width: 48.067vw;
          background: linear-gradient(184.9deg, #FFC665 0%, #FA8100 100%);
          border: 0.8vw solid rgba(255, 90, 0, 1);
          border-top: 0.267vw solid transparent;
          z-index: 2;
          margin-left: 0;
          .table-row {
            &:last-child {
              // height: 28vw;
              .think {
                padding: 5.333vw 3.2vw;
              }
            }
          }
        }
      }

      .comparison-col {
        margin-top: 2.667vw;
        .table-body {
          background: linear-gradient(135.91deg, #FFFFFF -1.3%, #F3F3F3 65.06%);
          border-top: 0;
          border: 0.267vw solid #D6D6D6;
          z-index: 1;
          width: 41.067vw;
        }
      }

      .cell {
        font-family: Poppins;
        font-weight: 700;
        font-style: Bold;
        letter-spacing: 0%;
        text-align: left;
        display: flex;
        align-items: center;
      }
      .table-header {
        // display: flex;
        // align-items: flex-end;
        .cell {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }

        .category {
          display: none;
        }

        .think {
          width: 48.067vw;
          height: 18.667vw;
          padding: 3.2vw 3.2vw;
          background: linear-gradient(0deg, #F95800 0%, #FF6D1D 100%);
          border-radius: 3.2vw 3.2vw 0 0;
          border-left: 0.8vw solid rgba(255, 90, 0, 1);
          border-top: 0.8vw solid rgba(255, 90, 0, 1);
          border-right: 0.8vw solid rgba(255, 90, 0, 1);
          font-size: 4.267vw;
          line-height: 18.667vw;
          color: #fff;
          margin-left: 0;
        }

        .competitor {
          width: 41.067vw;
          height: 16vw;
          padding: 3.2vw;
          background: linear-gradient(180deg, #F2F2F2 0%, #DCDCDC 100%);
          border-radius: 3.2vw 3.2vw 0 0;
          border-top: 0.267vw solid #D6D6D6;
          border-right: 0.267vw solid #D6D6D6;
          font-size: 3.2vw;
          // margin-left: -3.733vw;
          color: rgba(0, 0, 0, 0.9);
        }
      }
      .table-body {
        display: flex;
        flex-direction: column;
        position: relative;
        border-radius: 0 0 3.2vw 3.2vw;
        overflow: hidden;
        .table-row {
          display: flex;
          min-height: 16vw;
          height: auto;
          width: 100%;
          &:nth-child(odd) {
            background: transparent;
            border-bottom: 0.267vw solid rgba(255, 255, 255, 0.3);
          }
          &:nth-child(even) {
            background: rgba(255, 255, 255, 0.1);
            border-bottom: 0.267vw solid rgba(255, 255, 255, 0.3);
          }
          &:last-child {
            border-bottom: none;
          }
          .cell {
            width: 100%;
          }
          .think {
            padding: 3.2vw;
            font-size: 3.2vw;
            line-height: 3.733vw;
            color: #fff;
          }
          .competitor {
            padding: 3.2vw;
            font-size: 3.2vw;
            line-height: 3.733vw;
            color: rgba(0, 0, 0, 0.9);
          }
        }
        .table-row-competitor {
          &:nth-child(even) {
            background: transparent;
            border-bottom: 0.267vw solid rgba(255, 255, 255, 1)
          }
          &:nth-child(odd) {
            background: rgba(0, 0, 0, 0.03);
            border-bottom: 0.267vw solid rgba(255, 255, 255, 1)
          }
          &:last-child {
            border-bottom: none;
          }
        }
      }

      .swiper-section {
        width: 41.067vw;
        margin-left: -1.333vw;
        z-index: 0;
      }

      .check {
        width: 4.267vw;
        height: 4.267vw;
        background: url('~assets/images/common/comparison-true.png') no-repeat;
        background-size: 100% 100%;
        margin-right: 2.133vw;
        flex-shrink: 0;
      }
      .cross {
        width: 4.267vw;
        height: 4.267vw;
        background: url('~assets/images/common/comparison-false.png') no-repeat;
        background-size: 100% 100%;
        margin-right: 2.133vw;
        flex-shrink: 0;
      }
    }

    .btn-wrapper {
      position: relative;
      z-index: 1;
      width: 100%;
      margin-top: 8vw;
      padding: 5.333vw 0;
      border-radius: 53.333vw;
      font-family: Poppins;
      font-weight: 700;
      font-style: Bold;
      font-size: 20;
      color: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 90, 0, 1);
      cursor: pointer;

      &:active {
        background: rgba(255, 90, 0, 0.85);
      }
    }

    ::v-deep .swiper-pagination {
      position: relative !important;
      margin-top: 8vw;
      display: flex;
      justify-content: center;
      gap: 2.133vw;
      z-index: 1;
    }

    ::v-deep .swiper-pagination-bullet {
      width: 5.333vw !important;
      height: 1.6vw !important;
      border-radius: 2.133vw !important;
      background: rgba(0, 0, 0, 0.1) !important;
      opacity: 1 !important;
    }

    ::v-deep .swiper-pagination-bullet-active {
      background: #FF5A00 !important;
    }

    ::v-deep .swiper-custom-prev,
    ::v-deep .swiper-custom-next {
      display: none;
    }

    .comparison-top-bg {
      position: absolute;
      width: 100%;
      height: 46.133vw;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      background: url('~assets/images/common/comparison-top.webp') no-repeat;
      background-size: 100% 100%;
      z-index: 0;
    }

    .comparison-bottom-bg {
      display: none;
    }
  }
}

</style>
